// ===== ロット詳細 =====

import { useState } from "react";
import { Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import StyledButton from "../../../components/StyledButton";
import { idForDisplay } from "../../../util/uuidTextConverter";
import { materialSearchUrl } from "../../../util/Materialperser";
import { lotStatusLabel } from "../../../components/MaterialStatusBuilder";
import { userInfoCol } from "../../../util/userDisplay";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { DetailsByMaterial, LotRow, MaterialsById, capacityText, detailsOf, isLotShipped, normalizeMaterialId, packageFormText } from "./helpers";
import { materialLinkCell } from "./LotTable";
import { errorMessageOf, required } from "../../../util/typed";

export function LotDetailPage({ pageContext, materialsById, detailsByMaterial }: { pageContext: PageContext; materialsById: MaterialsById; detailsByMaterial: DetailsByMaterial }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id: carryOutId, lotId } = useParams<{ id: string; lotId: string }>();
  const [deleting, setDeleting] = useState(false);
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<LotRow>(() => {
    if (!carryOutId) {
      return Promise.resolve(null);
    }
    return api.carry_out.getFromId({ id: carryOutId }).then((co) => co.Lots.find((l) => l.id === lotId) ?? null);
  }, [api, carryOutId, lotId, retryCount]);

  return (
    <DetailGuard state={state} notFoundLabel={`材料ロット ${lotId}`} onRetry={retry}>
      {(lot) => (
        <LotDetailView
          lot={lot}
          navigate={navigate}
          api={api}
          carryOutId={required(carryOutId, "URLの出荷ID")}
          materialsById={materialsById}
          detailsByMaterial={detailsByMaterial}
          deleting={deleting}
          setDeleting={setDeleting}
        />
      )}
    </DetailGuard>
  );
}

function LotDetailView({ lot, navigate, api, carryOutId, materialsById, detailsByMaterial, deleting, setDeleting }: {
  lot: LotRow;
  navigate: ReturnType<typeof useNavigate>;
  api: PageContext["api"];
  carryOutId: string;
  materialsById: MaterialsById;
  detailsByMaterial: DetailsByMaterial;
  deleting: boolean;
  setDeleting: (value: boolean) => void;
}) {
  const shipped = isLotShipped(lot);
  const lotPk = lot.id;
  const lotDetails = detailsOf(detailsByMaterial, lot.material_id);

  const onDelete = () => {
    if (window.confirm(`材料ロット ${idForDisplay(lotPk)} を削除します。よろしいですか？`)) {
      setDeleting(true);
      api.lot.setDelete({ id: lotPk, deleted: true })
        .then(() => navigate(`/console/carry-out/${carryOutId}/`))
        .catch((e) => {
          console.warn("lot delete failed", e);
          setDeleting(false);
          api.showError(`材料ロットの削除に失敗しました。\n${errorMessageOf(e)}`);
        });
    }
  };

  const infoCols: InfoListColDef[] = [
    { label: "ID", context: idForDisplay(lot.id) },
    { label: "材料", context: materialLinkCell(materialsById, normalizeMaterialId(lot.material_id)) },
    { label: "LotNo", context: lot.lot_no ?? "-" },
    { label: "製造日", context: lot.manufactured_at != null ? new Time(lot.manufactured_at).Format("YYYY/MM/dd") : "-" },
    { label: "予定QR数", context: lot.material_count },
    { label: "貼付済材料数", context: `${shipped ? "✓ " : ""}${lot.Materials.length} / ${lot.material_count}`, targetUrl: lot.Materials.length > 0 ? new URL(materialSearchUrl(lot.Materials.map((m) => m.id)), window.location.origin) : undefined },
    { label: "出荷状況", context: lotStatusLabel(lot) },
    { label: "出荷日時", context: lot.shipped_at != null ? new Time(lot.shipped_at).Format("YYYY/MM/dd HH:mm:ss") : "-" },
    { label: "登録日", context: new Time(lot.createdAt).Format("YYYY/MM/dd HH:mm:ss") },
    userInfoCol(lot.User),
    { label: "備考", context: lot.remarks ?? "" },
  ];

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h4" fontWeight="bold">材料ロット情報</Typography>
        <Stack direction="row" gap={1}>
          <StyledButton buttonStyle="primary" onClick={() => navigate("scan/")} sx={{ width: 160 }}>{shipped ? "QRを再スキャン" : "QRを読み込み"}</StyledButton>
          <StyledButton buttonStyle="primary" onClick={() => navigate("edit/")} sx={{ width: 100 }}>編集</StyledButton>
          <Button variant="outlined" color="error" disabled={deleting} onClick={onDelete} sx={{ minWidth: 80 }}>削除</Button>
        </Stack>
      </Stack>
      <InfoList cols={infoCols} columns={3} />

      {lotDetails.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>構成細目</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            QR は <b>1 セット = 1 枚</b> で貼付されます。容量・荷姿はマスタで確定しています。
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>細目</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>荷姿</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">容量 (マスタ)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lotDetails.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell><Typography fontWeight="bold">{d.detail_name}</Typography></TableCell>
                  <TableCell>{packageFormText(d)}</TableCell>
                  <TableCell align="right">{capacityText(d)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
